<template>
  <div
    class="relative"
    :class="{
      'h-48': !isTextArea,
      'h-128': isTextArea,
    }"
  >
    <div class="relative z-30 h-full">
      <img
        v-if="hasSearchIcon && !isTextArea"
        src="/icons/search.png"
        class="h-24 w-24 absolute top-12 left-16 bottom-12 icon-search"
        alt="search icon"
      >
      <img
        v-if="hasClearIcon"
        src="~/assets/icons/delete.png"
        class="
          h-18
          w-18
          absolute
          top-12
          right-16
          bottom-12
          mt-2
          pointer-events-none
        "
        alt="clear icon"
      >
      <input
        v-if="!isTextArea"
        :id="uniqueId"
        :value="input"
        class="
          input-field
          border
          rounded-3xl
          h-full
          focus:bg-white focus:border-blue-50
          p-0
          px-24
        "
        :class="{
          'bg-picker': !isWhiteBg,
          'bg-white': isWhiteBg,
          '!pl-48': hasSearchIcon,
          'pr-48': (hasFocus && hasAction) || hasError || (isPasswordField && hasInput),
          'border-warning': hasError,
          'border-secondary': !hasError,
        }"
        :style="hasFocus && `border-color: ${borderColor}`"
        required="required"
        :placeholder="placeholder"
        :type="actualInputType"
        v-bind="type === 'number' ? { pattern: '\d*' } : {}"
        :autofocus="hasAutofocus"
        :autocomplete="autocomplete"
        @input="(e) => (input = e.target.value)"
        @blur="unsetFocus"
        @focus="setFocus"
        @keyup.enter="$emit('enter-submit')"
      >
      <!-- Password visibility toggle -->
      <button
        v-if="!isTextArea && isPasswordField && hasInput && !isLoading"
        type="button"
        class="absolute top-1/2 -translate-y-1/2 right-16 text-muted hover:text-standard transition-colors"
        tabindex="-1"
        @mousedown.prevent="showPassword = !showPassword"
      >
        <fa :icon="['far', showPassword ? 'eye' : 'eye-slash']" />
      </button>
      <textarea
        v-if="isTextArea"
        :id="uniqueId"
        :value="input"
        class="
          input-field
          border
          rounded-3xl
          h-full
          min-h-128
          focus:bg-white focus:border-blue-50
          py-16
          px-24
        "
        :class="{
          'bg-picker': !isWhiteBg,
          'bg-white': isWhiteBg,
          'border-warning': hasError,
          'border-secondary': !hasError,
        }"
        required="required"
        :placeholder="placeholder"
        :type="type"
        v-bind="type === 'number' ? { pattern: '\d*' } : {}"
        @input="(e) => (input = e.target.value)"
        @blur="unsetFocus"
        @focus="setFocus"
        @keyup.enter="$emit('enter-submit')"
      />
      <div
        v-if="!isTextArea && isLoading"
        class="
          h-32
          w-32
          rounded-full
          absolute
          top-8
          right-8
          bottom-8
          flex
          justify-center
          items-center
          text-sm
          bg-secondary
          text-primary
        "
      >
        <fa :icon="['fal', 'spinner']" :spin="true" />
      </div>
      <div
        v-else-if="!isTextArea && ((hasFocus && hasAction) || hasError)"
        class="
          h-32
          w-32
          rounded-full
          absolute
          top-8
          right-8
          bottom-8
          flex
          justify-center
          items-center
          text-sm
        "
        :class="{
          'bg-secondary text-primary select-none cursor-pointer': !hasError,
          'bg-warning text-white': hasError,
        }"
        @click="onSecondaryClick"
      >
        <fa
          :icon="[hasError ? 'fas' : 'far', hasError ? 'exclamation' : 'times']"
        />
      </div>
      <div
        v-else-if="!isTextArea && icon && iconStyle"
        class="
          h-32
          w-32
          rounded-full
          absolute
          top-8
          right-8
          bottom-8
          flex
          justify-center
          items-center
          text-sm text-white
          cursor-pointer
        "
        :style="`background-color: ${iconColor}`"
        @click="$emit('enter-submit')"
      >
        <fa :icon="[iconStyle, icon]" />
      </div>
    </div>
    <div
      class="
        absolute
        inset-selected-border
        rounded-3xl
        transition-all
        duration-200
      "
      :class="{
        'bg-orange-25': hasError,
        'bg-secondary': !hasError,
        'bg-transparent': !hasFocus && !hasError,
      }"
    />
  </div>
</template>

<script setup lang="ts">
import debounce from '~/util/debounce';

interface Props {
  placeholder: string,
  type?: string,
  initValue? : string,
  modelValue?: string,
  icon? : string,
  iconStyle? : string,
  borderColor? : string,
  iconColor? : string,
  hasSearchIcon? : boolean,
  hasClearIcon? : boolean,
  hasError? : boolean,
  hasAction? : boolean,
  isTextArea? : boolean,
  isLoading? : boolean,
  isWhiteBg? : boolean,
  hasAutofocus? : boolean,
  autocomplete?: string | null,
}

const props = withDefaults(defineProps<Props>(), {
  type: '',
  initValue : '',
  modelValue: '',
  icon : '',
  iconStyle : '',
  borderColor : '',
  iconColor : '',
  autocomplete: null,
});

const input = ref(props.modelValue || props.initValue);
const hasFocus = ref(false);
const uniqueId = ref('');
const submitInputDebounced = ref<any>(() => {});
const showPassword = ref(false);

const isPasswordField = computed(() => props.type === 'password');
const actualInputType = computed(() => {
  if (isPasswordField.value && showPassword.value) {
    return 'text';
  }
  return props.type;
});

const hasInput = computed<boolean>(()=> {
  return input.value.length > 0;
});

const emit = defineEmits<{
  (e: 'submitInput', payload: string): void;
  (e: 'update:modelValue', payload: string): void;
  (e: 'onFocus'): void;
  (e: 'onBlur'): void;
  (e: 'enterSubmit'): void;
}>();

onMounted(()=> {
  submitInputDebounced.value = debounce(submitInput, 300);
  uniqueId.value = Math.random().toString(36).substr(2, 9);
});

const submitInput = () => {
  emit('submitInput', input.value);
};

const setFocus = () => {
  emit('onFocus');
  hasFocus.value = true;
};
const unsetFocus = () => {
  emit('onBlur');
  setTimeout(() => {
    hasFocus.value = false;
  }, 200);
};
const onSecondaryClick = () => {
  if (!props.hasError) {
    input.value = '';
  }
};

watch(input, () => {
  submitInputDebounced.value();
  emit('update:modelValue', input.value);
});

watch(() => props.modelValue, (newVal) => {
  if (newVal !== undefined) {
    input.value = newVal;
  }
});

watch(() => props.initValue, (newVal) => {
  if (!props.modelValue) {
    input.value = newVal;
  }
});
</script>

<style scoped lang="postcss">
.input-field::placeholder {
  color: #696464;
  text-transform: none;
  font-size: 14px;
  letter-spacing: normal;
}

input {
  &::-webkit-inner-spin-button, &::-webkit-outer-spin-button {
    @apply hidden;
  }
}

</style>
